// Decisión node condition builder (editor overhaul L1). A Decisión branches on a
// value already known — a captured answer or the CRM context loaded at the start
// of the conversation. The author builds each branch with dropdowns
// ([variable] [operador] [valor]); this module turns that into the filtrex `when`
// the runtime evaluates, and parses an existing `when` back into the dropdowns.
//
// filtrex rule (engine/skills/expr.ts + memory): string literals use DOUBLE
// quotes; numbers/booleans are bare. Vars are addressed as `vars.<path>`.

import type { RawFlow } from './flows-data.js'

export interface FlowVar {
  /** The var path used in expressions, e.g. `presupuesto` or `lead.stage`. */
  value: string
  label: string
}

/** The variables a Decisión can branch on: every answer captured by an `ask`
 *  (`config.var`) or produced by an `act` (`config.out`) anywhere in the flow,
 *  plus the ambient CRM/contact roots the runtime always seeds (editor overhaul
 *  L0 — `vars.lead.*`, `vars.contact.*`). */
export function flowVars(flow: RawFlow): FlowVar[] {
  const out: FlowVar[] = []
  const seen = new Set<string>()
  const push = (value: string, label: string): void => {
    if (value && !seen.has(value)) {
      seen.add(value)
      out.push({ value, label })
    }
  }
  for (const n of flow.nodes) {
    if (n.type === 'ask' && typeof n.config.var === 'string') push(n.config.var, n.config.var)
    if (n.type === 'act' && typeof n.config.out === 'string') push(n.config.out, n.config.out)
  }
  push('lead.stage', 'Etapa en el proceso (CRM)')
  push('lead.is_new', '¿Es un contacto nuevo? (CRM)')
  push('lead.interest', 'Interés guardado (CRM)')
  push('contact.profile_name', 'Nombre del contacto')
  return out
}

export type CondOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'notempty'

export interface Condition {
  variable: string
  op: CondOp
  /** Ignored for notempty. */
  value: string
}

export const COND_OPS: { value: CondOp; label: string; needsValue: boolean }[] = [
  { value: 'eq', label: 'es igual a', needsValue: true },
  { value: 'ne', label: 'no es igual a', needsValue: true },
  { value: 'gt', label: 'es mayor que', needsValue: true },
  { value: 'gte', label: 'es mayor o igual a', needsValue: true },
  { value: 'lt', label: 'es menor que', needsValue: true },
  { value: 'lte', label: 'es menor o igual a', needsValue: true },
  // "tiene algún valor" = the var is set (truthy). For "está vacío", route the
  // "en cualquier otro caso" (default) branch instead — filtrex has no clean,
  // type-safe emptiness operator (`not vars.x` errors on non-booleans).
  { value: 'notempty', label: 'tiene algún valor', needsValue: false },
]

const OP_SYMBOL: Record<Exclude<CondOp, 'notempty'>, string> = {
  eq: '==',
  ne: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
}
const SYMBOL_OP: Record<string, Exclude<CondOp, 'notempty'>> = {
  '==': 'eq',
  '!=': 'ne',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
}

/** Format a literal: bare for numbers/booleans, double-quoted for strings. */
function formatValue(raw: string): string {
  const t = raw.trim()
  if (t === 'true' || t === 'false') return t
  if (/^-?\d+(?:\.\d+)?$/.test(t)) return t
  return `"${t.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

function unformatValue(raw: string): string {
  const t = raw.trim()
  const m = /^"((?:[^"\\]|\\.)*)"$/.exec(t)
  if (m) return m[1]!.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
  return t
}

/** Build the filtrex `when` for a structured condition. `notempty` is bare
 *  `vars.X` (filtrex returns the value → truthy when set), distinct from any
 *  comparison so a half-typed `eq` (`vars.X == ""`) is never confused with it. */
export function buildWhen(c: Condition): string {
  const path = `vars.${c.variable}`
  if (c.op === 'notempty') return path
  return `${path} ${OP_SYMBOL[c.op]} ${formatValue(c.value)}`
}

/** True for the default (“en cualquier otro caso”) branch: empty or `'true'`. */
export function isDefaultWhen(when: string | undefined): boolean {
  return !when || when.trim() === '' || when.trim() === 'true'
}

/** Parse an existing `when` back into the structured form. Returns null for a
 *  hand-written expression the builder can't represent (shown read-only). */
export function parseWhen(when: string): Condition | null {
  const t = when.trim()
  // "tiene algún valor" = bare `vars.X` (truthiness).
  const bare = /^vars\.([\w.]+)$/.exec(t)
  if (bare) return { variable: bare[1]!, op: 'notempty', value: '' }
  const m = /^vars\.([\w.]+)\s*(==|!=|>=|<=|>|<)\s*(.+)$/.exec(t)
  if (!m) return null
  const op = SYMBOL_OP[m[2]!]
  if (!op) return null
  // Only accept a clean literal RHS (quoted string / number / boolean); a
  // compound expression like `vars.b and true` can't be shown in the builder.
  const rhs = m[3]!.trim()
  const isLiteral =
    /^"(?:[^"\\]|\\.)*"$/.test(rhs) ||
    /^-?\d+(?:\.\d+)?$/.test(rhs) ||
    rhs === 'true' ||
    rhs === 'false'
  if (!isLiteral) return null
  return { variable: m[1]!, op, value: unformatValue(rhs) }
}
